import React, { useEffect, useState } from "react";
import api from "../lib/api";

import {
  CircularProgress,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  Button,
} from "@material-ui/core";
import MuiDialogContent from "@material-ui/core/DialogContent";
import AdminSummaryBox from "./AdminSummaryBox";
import defaultBackground from "../../assets/backgrounds/Indoor1.jpg";

import {
  StyledApproveButton,
  StyledRejectButton,
  StyledTableImage,
  StyledTableContainer,
  StyledImageContainer,
  StyledLoaderOverlay,
  StyledModalImageContainer,
} from "./common.styles";

const TABLE_HEADERS = [
  { label: "Name" },
  { label: "Company" },
  { label: "Email" },
  { label: "Comment" },
  { label: "Front" },
  { label: "Back" },
  { label: "Left" },
  { label: "Right" },
  { label: "Summary" },
  { label: "Action" },
];

const MIN_WIDTH = 100;

const AdminPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [imageDetails, setImageDetails] = useState({});

  const [modalDetailsOpen, setModalDetailsOpen] = useState(false);
  const [rowModalDetails, setRowModalDetails] = useState(null);

  const handleModalOpen = (imageDetails) => {
    setImageDetails(imageDetails);
    setModalOpen(true);
  };
  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleModalDetailsOpen = (row) => {
    setRowModalDetails(row);
    setModalDetailsOpen(true);
  };
  const handleModalDetailsClose = () => {
    setModalDetailsOpen(false);
  };

  const ImageCell = ({ src, background }) => (
    <TableCell align="center">
      <Box display="flex" flexDirection="row" flex="1" justifyContent="center">
        <StyledImageContainer background={background}>
          <StyledTableImage
            src={src}
            onClick={() => handleModalOpen({ src, background })}
          />
        </StyledImageContainer>
      </Box>
    </TableCell>
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const response = await api.get("/listSubmissions");
      const rowsData = JSON.parse(response.data.message);
      const rowsDataWithMappedImages = rowsData
        .map((row) => {
          const newFields = {};
          row.images.map((image, i) => {
            newFields[`image${i + 1}`] = image;
          });
          return {
            ...row,
            ...newFields,
            background: row.backgroundImage
              ? row.backgroundImage
              : defaultBackground,
          };
        })
        .sort((a, b) => a.id - b.id);

      setLoading(false);
      setRows(rowsDataWithMappedImages.reverse());
    };

    fetchData();
  }, []);

  const onApprove = async (id) => {
    setLoading(true);
    try {
      await api.put("/approveSubmission", {
        id,
      });

      const newRows = rows.map((row) => {
        if (row.id === id) {
          return { ...row, approved: true, rejected: false };
        }
        return row;
      });
      setRows(newRows);
      setLoading(false);
    } catch (error) {
      console.log("error: ", error);
    }
  };
  const onReject = async (id) => {
    setLoading(true);
    try {
      await api.put("/rejectSubmission", {
        id,
      });

      const newRows = rows.map((row) => {
        if (row.id === id) {
          return { ...row, approved: false, rejected: true };
        }
        return row;
      });
      setRows(newRows);
      setLoading(false);
    } catch (error) {
      console.log("error: ", error);
    }
  };

  return (
    <>
      <Box
        width="100%"
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <StyledTableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {TABLE_HEADERS.map((header) => (
                  <TableCell align="center" style={{ fontWeight: "bold" }}>
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell align="center" style={{ minWidth: MIN_WIDTH }}>
                    {row.name}
                  </TableCell>
                  <TableCell align="center" style={{ minWidth: MIN_WIDTH }}>
                    {row.company}
                  </TableCell>
                  <TableCell align="center" style={{ minWidth: MIN_WIDTH }}>
                    {row.email}
                  </TableCell>
                  <TableCell align="center" style={{ minWidth: MIN_WIDTH }}>
                    {row.comment}
                  </TableCell>
                  <ImageCell src={row.image1} background={row.background} />
                  <ImageCell src={row.image2} background={row.background} />
                  <ImageCell src={row.image3} background={row.background} />
                  <ImageCell src={row.image4} background={row.background} />
                  <TableCell align="center">
                    <Button onClick={() => handleModalDetailsOpen(row)}>
                      Details
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      display="flex"
                      flexDirection="row"
                      flex="1"
                      justifyContent="center"
                    >
                      <StyledApproveButton
                        variant="outlined"
                        style={{ marginRight: 5 }}
                        selected={row.approved}
                        disabled={row.approved || row.rejected}
                        onClick={() => onApprove(row.id)}
                        showSelectedStyles={!row.approved}
                        loading={loading}
                      >
                        <Typography bold>
                          {row.approved ? "APPROVED" : "APPROVE"}
                        </Typography>
                      </StyledApproveButton>
                      <StyledRejectButton
                        variant="outlined"
                        selected={row.rejected}
                        disabled={row.approved || row.rejected}
                        onClick={() => onReject(row.id)}
                        showSelectedStyles={!row.rejected}
                        loading={loading}
                      >
                        <Typography bold>
                          {row.rejected ? "REJECTED" : "REJECT"}
                        </Typography>
                      </StyledRejectButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {loading ? (
            <StyledLoaderOverlay>
              <CircularProgress />
            </StyledLoaderOverlay>
          ) : null}
        </StyledTableContainer>
      </Box>
      <Dialog
        onClose={handleModalClose}
        open={modalOpen}
        fullWidth={true}
        maxWidth={"lg"}
      >
        <MuiDialogContent>
          <Box>
            <StyledModalImageContainer
              src={imageDetails.src}
              background={imageDetails.background}
            />
          </Box>
        </MuiDialogContent>
      </Dialog>

      {modalDetailsOpen ? (
        <Dialog
          open={modalDetailsOpen}
          fullWidth={true}
          maxWidth={"lg"}
          onClose={handleModalDetailsClose}
        >
          <AdminSummaryBox
            fullWidth={true}
            maxWidth={"sm"}
            open={modalDetailsOpen}
            row={rowModalDetails}
          />
        </Dialog>
      ) : null}
    </>
  );
};

export default AdminPanel;
